// STEP 3-SHIP-D-4/D-5 · CJ 결과 자동 등록 폴러.
// 페어링된 Windows Agent 로부터 파싱된 tracking rows 를 주기적으로 가져와
// 기존 /api/seller/orders/bulk-invoice-preview + /bulk-invoice 로 등록한다.
//
// - Backend/Agent 는 새로 만들지 않는다. 기존 backend API 를 REUSE.
// - LIVEMOA 로그인 token 은 Agent 로 넘기지 않는다 (§20).
// - Conflict / 미등록 / 다른 tracking 이 이미 존재하는 경우 자동 overwrite 하지 않는다 (§16).
//   → 해당 row 를 SKIPPED 로 Agent 에 ack + 사용자 toast.
// - Exactly-once: (order, carrier, tracking) 이미 등록된 경우 no-op + ack APPLIED.
//
// 이 컴포넌트는 화면을 렌더링하지 않는다 — SellerDashboard 하위 어디에서 마운트해도
// 안전한 background hook 이다.
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { checkAgent, getPendingResults, ackResults } from '@/lib/agentBridge';

const POLL_INTERVAL_MS = 15000;   // 15s — burden-light for both Agent + LIVEMOA
const CARRIER_LABEL = { CJ: 'CJ대한통운', HANJIN: '한진택배', LOTTE: '롯데택배' };
const CARRIER_LOCK_TOAST_ID = 'agent-auto-import';

export default function AgentAutoImport() {
  const runningRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const tickOnce = async () => {
      if (runningRef.current) return;
      runningRef.current = true;
      try {
        const h = await checkAgent();
        if (!h.available || !h.paired) return;

        let pend;
        try { pend = await getPendingResults(200); }
        catch { return; }  // Agent offline/unpaired mid-tick — retry next interval.
        const rows = pend?.rows || [];
        if (!rows.length) return;

        // Group by carrier so we call bulk-invoice per carrier (backend expects
        // one courier value per submission).
        const byCarrier = rows.reduce((acc, r) => {
          const c = r.carrier || 'CJ';
          (acc[c] = acc[c] || []).push(r);
          return acc;
        }, {});

        for (const [carrier, carrierRows] of Object.entries(byCarrier)) {
          if (cancelled) return;
          await applyBatch(carrier, carrierRows);
        }
      } finally {
        runningRef.current = false;
      }
    };

    const applyBatch = async (carrier, carrierRows) => {
      const courierLabel = CARRIER_LABEL[carrier] || carrier;
      const previewRows = carrierRows.map((r) => ({
        order_number: r.order_number,
        courier: courierLabel,
        tracking_number: r.tracking_number,
      }));

      let preview;
      try {
        const r = await api.post('/seller/orders/bulk-invoice-preview', { rows: previewRows });
        preview = r.data;
      } catch (e) {
        // LIVEMOA 세션 만료/네트워크 — 이번 tick 건너뛰고 다음 tick 재시도.
        return;
      }

      const perLine = preview?.rows || [];
      // conflict 판단: `duplicate_tracking_in_db` or `not_shippable` (기존 다른 tracking 존재)
      const acks = [];
      const appliableIdx = [];
      const skippedIdx = [];
      perLine.forEach((line, i) => {
        const src = carrierRows[i];
        if (!src) return;
        const errs = line.errors || [];
        const hasConflict = errs.some((e) =>
          ['duplicate_tracking_in_db', 'ambiguous', 'wrong_seller'].includes(e.code));
        const notFound = errs.some((e) => e.code === 'not_found');
        const notShippable = errs.some((e) => e.code === 'not_shippable');
        if (hasConflict) {
          acks.push({ row_pk: src.row_pk, state: 'SKIPPED',
                      error: `conflict: ${errs.map((x) => x.code).join(',')}` });
          skippedIdx.push(i);
        } else if (notFound) {
          acks.push({ row_pk: src.row_pk, state: 'SKIPPED', error: 'not_found' });
          skippedIdx.push(i);
        } else if (notShippable) {
          // Order might be in shipped/delivered already · treat as APPLIED (idempotent).
          acks.push({ row_pk: src.row_pk, state: 'APPLIED', error: 'idempotent_not_shippable' });
        } else if (line.is_valid) {
          appliableIdx.push(i);
        } else {
          acks.push({ row_pk: src.row_pk, state: 'FAILED',
                      error: errs.map((x) => x.code).join(',') || 'invalid' });
        }
      });

      // 실제 apply 호출.
      let appliedCt = 0;
      if (appliableIdx.length > 0) {
        const items = appliableIdx.map((i) => {
          const line = perLine[i];
          return {
            seller_order_id: line.seller_order_id,
            courier: line.courier,
            tracking_number: line.tracking_number,
          };
        });
        try {
          const r = await api.post('/seller/orders/bulk-invoice', { items });
          const applied = r.data?.applied || appliableIdx.length;
          appliedCt = applied;
          appliableIdx.forEach((i) => {
            const src = carrierRows[i];
            if (src) acks.push({ row_pk: src.row_pk, state: 'APPLIED' });
          });
        } catch (e) {
          appliableIdx.forEach((i) => {
            const src = carrierRows[i];
            if (src) acks.push({ row_pk: src.row_pk, state: 'FAILED',
                                 error: (e?.response?.data?.detail || 'apply_failed').slice(0, 200) });
          });
        }
      }

      try { await ackResults(acks); } catch { /* Agent transient offline — will retry */ }

      if (appliedCt > 0) {
        toast.success(`${courierLabel} 송장 ${appliedCt}건 자동 등록 완료`, {
          id: CARRIER_LOCK_TOAST_ID,
        });
      }
      if (skippedIdx.length > 0) {
        toast.warning(`${courierLabel} 송장 ${skippedIdx.length}건 확인 필요 (충돌/미매칭)`, {
          id: `${CARRIER_LOCK_TOAST_ID}-skip`,
        });
      }
    };

    // Kick off immediately and then on interval.
    const timer = setInterval(tickOnce, POLL_INTERVAL_MS);
    tickOnce();
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  return null;
}
